import { forwardRef, lazy, Suspense } from 'react'
import { cn } from '@shared/lib/cn'
import { Label } from '../Label'
import type { PhoneInputProps } from './PhoneInput'

// Lazily load the real PhoneInput so libphonenumber-js (~100 KB gzip of phone
// metadata) ships in its own chunk, fetched only when a phone field renders —
// not in the entry bundle via the `@shared/ui` barrel.
const PhoneInputImpl = lazy(() => import('./PhoneInput').then((m) => ({ default: m.PhoneInput })))

/** Static placeholder shown while the PhoneInput chunk loads. Mirrors the real
 *  control's label + `h-10` bordered box so there is no layout shift. */
function PhoneInputSkeleton({
  label,
  required,
  wrapperClassName,
  className,
}: Pick<PhoneInputProps, 'label' | 'required' | 'wrapperClassName' | 'className'>) {
  return (
    <div className={cn('flex flex-col gap-1', wrapperClassName)}>
      {label && <Label required={required}>{label}</Label>}
      <div
        className={cn(
          'border-border bg-surface h-10 w-full animate-pulse rounded-lg border',
          className
        )}
      />
    </div>
  )
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>((props, ref) => (
  <Suspense
    fallback={
      <PhoneInputSkeleton
        label={props.label}
        required={props.required}
        wrapperClassName={props.wrapperClassName}
        className={props.className}
      />
    }
  >
    <PhoneInputImpl {...props} ref={ref} />
  </Suspense>
))
PhoneInput.displayName = 'PhoneInput'
